import { useEffect, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

// Debounce utility
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number): T {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  return ((...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  }) as T;
}

export function useUnreadMessages() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const conversationIdsRef = useRef<string[]>([]);

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["unread-messages", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      // Fetch user's conversation IDs
      const { data: conversations } = await supabase
        .from("direct_conversations")
        .select("id")
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`);

      if (!conversations?.length) {
        conversationIdsRef.current = [];
        return 0;
      }

      conversationIdsRef.current = conversations.map(c => c.id);

      // Get unread count
      const { count } = await supabase
        .from("direct_messages")
        .select("*", { count: "exact", head: true })
        .in("conversation_id", conversationIdsRef.current)
        .eq("read", false)
        .neq("sender_id", user.id);

      console.log(`[UnreadMessages] User ${user.id}: ${count} unread messages across ${conversationIdsRef.current.length} conversations`);
      return count || 0;
    },
    enabled: !!user?.id,
    staleTime: 30000, // 30 seconds
  });

  useEffect(() => {
    if (!user?.id) return;

    // Debounced invalidation to prevent rapid refetches
    const debouncedInvalidate = debounce(() => {
      queryClient.invalidateQueries({ queryKey: ["unread-messages", user.id] });
    }, 300);

    // Subscribe to real-time updates - scoped to user's involvement
    const channel = supabase
      .channel(`unread_messages_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "direct_messages",
        },
        (payload) => {
          // Only invalidate if this message is in one of user's conversations
          const conversationId = (payload.new as any)?.conversation_id || (payload.old as any)?.conversation_id;
          if (conversationId && conversationIdsRef.current.includes(conversationId)) {
            debouncedInvalidate();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  return unreadCount;
}
